import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import useCurrencyCode from '../../hooks/useCurrencyCode';

const STATUS_OPTIONS = ['pending', 'packed', 'paid', 'collected'];

function StatusBadge({ status }) {
  const tone =
    status === 'paid'
      ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/50'
      : status === 'packed'
      ? 'bg-amber-500/15 text-amber-200 border-amber-500/50'
      : status === 'collected'
      ? 'bg-blue-500/15 text-blue-200 border-blue-500/50'
      : 'bg-slate-800 text-slate-200 border-slate-700';

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${tone}`}>
      {status}
    </span>
  );
}

function ActionButton({ label, onClick, disabled, loading, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-left transition hover:border-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="text-sm font-semibold text-white">{loading ? 'Sending...' : label}</span>
      <span className="text-xs text-slate-400">{hint}</span>
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-sm">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{label}</span>
      <span className="text-right text-slate-100">{value || '-'}</span>
    </div>
  );
}

export default function LitePreorders() {
  const currencyCode = useCurrencyCode();
  const [preorders, setPreorders] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pickupTime, setPickupTime] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [showPackingModal, setShowPackingModal] = useState(false);

  const totalFor = useMemo(
    () => (order) =>
      formatCurrency(
        order.totalAmount ||
          (order.items || []).reduce((sum, item) => sum + Number(item.subtotal || 0), 0),
        currencyCode
      ),
    [currencyCode]
  );

  const fetchPreorders = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get('/preorders');
      setPreorders(res.data.rows || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load pre-orders.');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await api.get(`/preorders/${id}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to load pre-order.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreorders();
  }, [fetchPreorders]);

  useEffect(() => {
    if (selectedId) {
      fetchDetail(selectedId);
      setPickupTime('');
      setShowPackingModal(false);
    } else if (preorders.length > 0) {
      setSelectedId(preorders[0].id);
    }
  }, [selectedId, fetchDetail, preorders]);

  const handleStatusChange = async (nextStatus) => {
    if (!detail) return;
    setUpdatingStatus(true);
    try {
      await api.patch(`/preorders/${detail.id}/status`, { status: nextStatus });
      toast.success('Status updated.');
      const updated = { ...detail, status: nextStatus };
      setDetail(updated);
      setPreorders((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, status: nextStatus } : p))
      );
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const triggerNotification = async (event, extraBody = {}) => {
    if (!detail) return;
    setActionLoading((prev) => ({ ...prev, [event]: true }));
    try {
      await api.post(`/preorders/${detail.id}/${event}`, { pickupTime: pickupTime || null, ...extraBody });
      toast.success('Notification sent.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to trigger notification.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [event]: false }));
    }
  };

  const canSendInvoice = detail?.status === 'pending';
  const canSendReceipt = detail && ['paid', 'collected'].includes(detail.status);
  const canSendPickup = detail && ['packed', 'paid', 'collected'].includes(detail.status);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-emerald-300">Operations</p>
          <h1 className="text-2xl font-semibold text-white">Pre-orders (Lite)</h1>
          <p className="text-sm text-slate-400">Cards for quick glance, status, and actions.</p>
        </div>
        <button
          type="button"
          onClick={fetchPreorders}
          className="rounded-full border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:border-emerald-400"
        >
          Refresh
        </button>
      </header>

      <section className="space-y-3">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{preorders.length} pre-order{preorders.length === 1 ? '' : 's'}</span>
          <span className="rounded-full border border-slate-800 bg-slate-900 px-3 py-1">
            Mobile cards
          </span>
        </div>
        <div className="space-y-3">
          {loadingList && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
              Loading pre-orders...
            </div>
          )}
          {!loadingList && preorders.length === 0 && (
            <div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 text-center text-sm text-slate-400">
              No pre-orders yet.
            </div>
          )}
          {!loadingList &&
            preorders.map((order) => (
              <button
                key={order.id}
                type="button"
                onClick={() => {
                  setSelectedId(order.id);
                  setShowDetail(true);
                }}
                className={`flex w-full flex-col gap-2 rounded-2xl border px-4 py-3 text-left transition ${
                  selectedId === order.id && showDetail
                    ? 'border-emerald-500/60 bg-emerald-600/10 shadow-[0_15px_40px_-25px_rgba(16,185,129,0.8)]'
                    : 'border-slate-800 bg-slate-900/70 hover:border-emerald-400/70'
                }`}
              >
                <div className="flex items-start justify-between gap-3 w-full">
                  <div className="flex min-w-0 flex-col">
                    <span className="text-sm font-semibold text-white">{order.customerName}</span>
                    <span className="text-xs text-slate-400">{order.customerWhatsApp || 'No contact'}</span>
                  </div>
                  <div className="shrink-0 self-start">
                    <StatusBadge status={order.status} />
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-slate-200">
                  <span className="font-semibold">{totalFor(order)}</span>
                  <span className="text-xs text-slate-400">
                    {new Date(order.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span className="capitalize">{order.source}</span>
                  <span>#{order.id}</span>
                </div>
              </button>
            ))}
        </div>
      </section>

      {showDetail && (
        <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur px-3 py-4">
          <div className="mx-auto flex h-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.24em] text-emerald-300">Pre-order</p>
                <p className="text-lg font-semibold text-white">
                  #{detail?.id} - {detail?.customerName}
                </p>
                <p className="text-xs text-slate-400">
                  {detail?.customerEmail || 'No email'} - {detail?.customerWhatsApp || 'No WhatsApp'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {detail && <StatusBadge status={detail.status} />}
                <button
                  type="button"
                  onClick={() => {
                    setShowDetail(false);
                    setShowPackingModal(false);
                  }}
                  className="rounded-full border border-slate-700 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:border-slate-500"
                >
                  Close
                </button>
              </div>
            </div>

            {detailLoading && (
              <div className="flex flex-1 items-center justify-center text-sm text-slate-400">Loading pre-order...</div>
            )}

            {detail && !detailLoading && (
              <div className="flex flex-1 flex-col gap-3 overflow-hidden px-4 py-3">
                <div className="space-y-2 rounded-2xl border border-slate-800 bg-slate-950/60 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Status</span>
                    <select
                      value={detail.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      disabled={updatingStatus}
                      className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Pickup time (optional)</span>
                    <input
                      type="datetime-local"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    <span className="text-[11px] text-slate-500">Used for receipt/pickup notifications.</span>
                  </div>
                  <InfoRow label="Notes" value={detail.notes} />
                  <InfoRow label="Address" value={detail.customerAddress} />
                  <InfoRow label="Created" value={new Date(detail.createdAt).toLocaleString()} />
                </div>

                <div className="rounded-2xl border border-slate-800 bg-slate-950/60">
                  <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Packing list</span>
                      <span className="text-sm text-slate-200">
                        {(detail.items || []).length} item{(detail.items || []).length === 1 ? '' : 's'} | {totalFor(detail)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPackingModal(true)}
                      className="rounded-full border border-emerald-500 px-3 py-1 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/10"
                    >
                      View list
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 rounded-2xl bg-slate-900/80 p-3">
                  <ActionButton
                    label="Invoice"
                    disabled={!canSendInvoice || actionLoading['send-invoice']}
                    loading={actionLoading['send-invoice']}
                    onClick={() => triggerNotification('send-invoice')}
                    hint="Only when pending"
                  />
                  <ActionButton
                    label="Receipt & Pickup"
                    disabled={!canSendReceipt || actionLoading['send-receipt']}
                    loading={actionLoading['send-receipt']}
                    onClick={() => triggerNotification('send-receipt', { pickupTime: pickupTime || null })}
                    hint="After payment"
                  />
                  <ActionButton
                    label="Pickup Ready"
                    disabled={!canSendPickup || actionLoading['send-pickup']}
                    loading={actionLoading['send-pickup']}
                    onClick={() => triggerNotification('send-pickup', { pickupTime: pickupTime || null })}
                    hint="When packed"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPackingModal && detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 px-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">Packing list</p>
                <p className="text-sm text-slate-200">
                  #{detail.id} - {detail.customerName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowPackingModal(false)}
                className="rounded-full border border-slate-700 px-3 py-1 text-xs font-semibold text-slate-200 transition hover:border-slate-500"
              >
                Close
              </button>
            </div>
            <div className="max-h-[70vh] overflow-auto divide-y divide-slate-800">
              {(detail.items || []).map((item) => (
                <div
                  key={`${item.productName}-${item.id || item.productId || item.subtotal}`}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div>
                    <p className="font-semibold text-white">{item.productName}</p>
                    <p className="text-xs text-slate-400">
                      Qty {item.quantity} - {formatCurrency(item.unitPrice, currencyCode)} each
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-white">
                    {formatCurrency(item.subtotal, currencyCode)}
                  </p>
                </div>
              ))}
              {(detail.items || []).length === 0 && (
                <p className="px-4 py-3 text-sm text-slate-400">No items.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

